// Shared-ad-account attribution readiness (§3b), for BOTH Meta and Google.
// Several kyck Accounts can share ONE Meta ad account (`fbAdAccountId`) or ONE
// Google Ads customer (`googleAdsCustomerId`), and each platform's reporting
// returns the WHOLE ad account's campaigns. Each location attributes its own
// campaigns via `adAccountMatchTokens` (a case-insensitive substring match on the
// campaign name). So a location that SHARES on EITHER platform but has NO match
// token is a spend-attribution GAP — its spend can't be split out of the cluster
// until a token is added.
//
// ⚠️ ONE TOKEN LIST, BOTH PLATFORMS. `Account.adAccountMatchTokens` drives Meta
// AND Google attribution deliberately (see googleAdsAttribution.ts's header: a
// separate `googleAdsMatchTokens` would double the admin work per location and
// drift, and a stale token set attributes silently and WRONGLY). That is why
// "has a token" here is BINARY regardless of which platform is shared — a token
// added for a Google-shared cluster is the same token Meta would use.
//
// Sharedness is GROUND TRUTH: an id held by ≥2 accounts is shared, independent of
// the `isSharedAdAccount` affordance flag (which stays a manual override for the
// pre-sharing case). This module is the ONE place that decides "shared" + "has a
// token", so the Accounts grid column/filter, the AccountForm "shared with X other
// locations" alert and the AccountDetail read view agree by construction.
// Pure + null-safe; Meta ids normalized via normalizeAdAccountId so a
// raw-vs-act_-prefixed store never mis-groups.

import { normalizeAdAccountId } from './adAccountDrift.js';

export type AdAccountSharingStatus =
  // No ad account set on EITHER platform — nothing to share, nothing to
  // attribute ('—').
  | 'no-ad-account'
  // Every id this account holds is held by it alone (count 1) — attribution is
  // unambiguous without tokens.
  | 'not-shared'
  // Shared cluster (≥2 holders) on EITHER platform AND this account has ≥1 match
  // token — attribution ready.
  | 'shared-token-set'
  // Shared cluster (≥2 holders) on EITHER platform AND this account has NO match
  // token — the gap to fix (its spend can't be attributed).
  | 'shared-no-token';

// Which platform(s) the account is actually shared on. An adjunct to the status
// (never a replacement) so a surface can name WHICH side the gap is on without
// the four status values changing.
export type AdAccountSharingPlatform = 'meta' | 'google';

export type AdAccountSharing = {
  status: AdAccountSharingStatus;
  // Empty unless the status is one of the two shared-* values.
  sharedOn: AdAccountSharingPlatform[];
};

// Google shows customer ids as `123-456-7890` while our store holds bare digits,
// so a hand-typed dashed id must still group with its bare twin; an empty/garbage
// value normalises to null rather than '', which would otherwise collapse every
// unset account into one giant fake "shared" cluster.
//
// ⚠️ Deliberately INLINED rather than imported from googleAdsAttribution.ts —
// that module already imports `hasMatchToken` from here, so importing back would
// be a cycle (and would drag the reporting type graph into this lean lib).
// Keep in sync with normalizeCustomerId in src/lib/googleAdsAttribution.ts.
export function normalizeGoogleCustomerId(id: string | null | undefined): string | null {
  if (id == null) return null;
  const digits = String(id).replace(/\D/g, '');
  return digits === '' ? null : digits;
}

// Per-platform holder counts, keyed by the platform's normalized id.
export type AdAccountHolderCounts = {
  meta: Map<string, number>;
  google: Map<string, number>;
};

// The lean roster shape the classifier reads.
export type SharingAccount = {
  fbAdAccountId?: string | null;
  googleAdsCustomerId?: string | null;
  adAccountMatchTokens?: readonly string[] | null;
};

// True when at least one token is a non-empty (trimmed) string. Empty strings and
// null/undefined all count as "no token" — the SAME token check AccountForm uses.
export function hasMatchToken(tokens: readonly string[] | null | undefined): boolean {
  return (tokens ?? []).some((t) => t.trim() !== '');
}

// Group the roster by normalized ad-account id on EACH platform → number of
// holders. Accounts with no id on a platform are skipped for that platform (they
// hold nothing there). The count is what makes a cluster "shared" (≥2). Built once
// over the FULL roster (not a page slice) so the shared verdict is correct
// roster-wide.
export function buildAdAccountHolderCounts(
  accounts: ReadonlyArray<Pick<SharingAccount, 'fbAdAccountId' | 'googleAdsCustomerId'>>,
): AdAccountHolderCounts {
  const meta = new Map<string, number>();
  const google = new Map<string, number>();
  for (const a of accounts) {
    const metaKey = normalizeAdAccountId(a.fbAdAccountId);
    if (metaKey) meta.set(metaKey, (meta.get(metaKey) ?? 0) + 1);
    const googleKey = normalizeGoogleCustomerId(a.googleAdsCustomerId);
    if (googleKey) google.set(googleKey, (google.get(googleKey) ?? 0) + 1);
  }
  return { meta, google };
}

// Classify one account against the roster's holder counts. A single-holder id is
// NEVER "shared" (count < 2), so it can't be a missing-token gap. Shared on
// EITHER platform is enough — one token list serves both, so a Google-shared
// location with its own Meta ad account still needs (and shows) the gap.
export function classifyAdAccountSharing(
  account: SharingAccount,
  counts: AdAccountHolderCounts,
): AdAccountSharing {
  const metaKey = normalizeAdAccountId(account.fbAdAccountId);
  const googleKey = normalizeGoogleCustomerId(account.googleAdsCustomerId);
  if (!metaKey && !googleKey) return { status: 'no-ad-account', sharedOn: [] };

  const sharedOn: AdAccountSharingPlatform[] = [];
  if (metaKey && (counts.meta.get(metaKey) ?? 0) >= 2) sharedOn.push('meta');
  if (googleKey && (counts.google.get(googleKey) ?? 0) >= 2) sharedOn.push('google');
  if (sharedOn.length === 0) return { status: 'not-shared', sharedOn: [] };

  // One shared token list — so the token check is binary, whichever platform(s)
  // the sharing is on.
  const status = hasMatchToken(account.adAccountMatchTokens)
    ? 'shared-token-set'
    : 'shared-no-token';
  return { status, sharedOn };
}

// The one status the grid filter isolates: shared cluster (either platform), no
// match token. The account whose spend can't be attributed until a token is added.
export function isSharedMissingMatchToken(status: AdAccountSharingStatus): boolean {
  return status === 'shared-no-token';
}

// Human label for the platform(s) an account is shared on — "Meta", "Google" or
// "Meta and Google". Shared so the grid tooltip, the form hint and the read view
// word it identically. Empty list -> '' (the caller renders nothing).
export function describeSharedOn(sharedOn: readonly AdAccountSharingPlatform[]): string {
  const names = sharedOn.map((p) => (p === 'meta' ? 'Meta' : 'Google'));
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  return names.join(' and ');
}

// One shared cluster this account sits in, on ONE platform. `count` includes this
// account; `tokensSet` is how many of those holders have ≥1 match token — the
// completeness hint the AccountForm shows ("shared by 3 — 1 has tokens set").
export type SharedCluster = {
  platform: AdAccountSharingPlatform;
  count: number;
  tokensSet: number;
};

// The AccountForm's live view of the same ground truth the grid uses, keyed on
// the CURRENTLY-typed ids rather than the stored ones (so the hint reacts as the
// AM types). `others` is the roster MINUS this account.
//
// Returns one entry per platform this account is actually shared on (empty when
// it is shared on neither), because the two clusters are different sets of
// locations with different counts — collapsing them would misstate both.
export function describeSharedClusters(
  self: SharingAccount,
  others: ReadonlyArray<SharingAccount>,
): SharedCluster[] {
  const selfHasTokens = hasMatchToken(self.adAccountMatchTokens);

  const cluster = (
    platform: AdAccountSharingPlatform,
    key: string | null,
    keyOf: (a: SharingAccount) => string | null,
  ): SharedCluster | null => {
    if (!key) return null;
    const siblings = others.filter((a) => keyOf(a) === key);
    if (siblings.length === 0) return null; // sole holder — not shared
    return {
      platform,
      count: siblings.length + 1, // + this account
      tokensSet:
        siblings.filter((a) => hasMatchToken(a.adAccountMatchTokens)).length +
        (selfHasTokens ? 1 : 0),
    };
  };

  return [
    cluster('meta', normalizeAdAccountId(self.fbAdAccountId), (a) =>
      normalizeAdAccountId(a.fbAdAccountId),
    ),
    cluster('google', normalizeGoogleCustomerId(self.googleAdsCustomerId), (a) =>
      normalizeGoogleCustomerId(a.googleAdsCustomerId),
    ),
  ].filter((c): c is SharedCluster => c !== null);
}

// Whether the AccountForm shows the match-token field. Detected sharing on EITHER
// platform is enough — that is the fix for "there's no signposted way to add a
// Google match token": the box simply appears, with no need to tick a checkbox
// describing a Meta situation the account isn't in. The flag remains a manual
// override for the pre-sharing case (ticked before the second sibling exists).
export function shouldShowMatchTokens(
  clusters: readonly SharedCluster[],
  isSharedAdAccountFlag: boolean,
): boolean {
  return clusters.length > 0 || isSharedAdAccountFlag;
}

// Convenience: classify every account against the counts derived from the same
// roster. Returns a map keyed by account id (what the grid context stores).
export function buildAdAccountSharingMap(
  accounts: ReadonlyArray<SharingAccount & { id: string }>,
): Map<string, AdAccountSharing> {
  const counts = buildAdAccountHolderCounts(accounts);
  const out = new Map<string, AdAccountSharing>();
  for (const a of accounts) out.set(a.id, classifyAdAccountSharing(a, counts));
  return out;
}
